import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useSession } from '@/lib/session-context'
import { LoginForm } from '@/components/auth/LoginForm'
import { SignupForm } from '@/components/auth/SignupForm'
import { CheckInboxPanel } from '@/components/auth/CheckInboxPanel'
import { TwoFactorChallengeForm } from '@/components/auth/TwoFactorChallengeForm'

const HEADERS = {
  login: {
    title: 'System Login',
    subtitle: 'Authenticate to access your execution environment.',
  },
  signup: {
    title: 'Initialise Profile',
    subtitle: 'Create your operator account to begin the activation sequence.',
  },
}

const TRANSITION = 'animate-in fade-in slide-in-from-right-5 duration-500 ease-out'
const SIGN_IN_PATH = '/sign-in'
const SIGN_UP_PATH = '/sign-up'
const CONFIRMATION_SENT_PATH = '/confirmation-sent'
const VERIFY_TWO_FACTOR_PATH = '/verify-2fa'
const CONFIRMATION_EMAIL_KEY = 'sentinel.confirmationEmail'
const CONFIRMATION_MESSAGES = {
  confirmed: 'Confirmation complete. Sign in to continue.',
  already_redeemed: 'Confirmation token already redeemed. Sign in to continue.',
  unconfirmed: 'Verify your account confirmation email before signing in.',
}
const TOAST_CONFIRMATION_STATUSES = ['confirmed', 'already_redeemed']

export function AuthShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const [view, setView] = useState('login')
  const [pendingEmail, setPendingEmail] = useState(
    location.state?.email ?? sessionStorage.getItem(CONFIRMATION_EMAIL_KEY),
  )
  const confirmationToastKeyRef = useRef(null)
  const { signIn, signUp, verifyTwoFactor, verifyTwoFactorBackup } = useSession()
  const search = location.search
  const confirmationStatus = new URLSearchParams(search).get('confirmation')
  const confirmationMessage = CONFIRMATION_MESSAGES[confirmationStatus]
  const shouldToastConfirmation = TOAST_CONFIRMATION_STATUSES.includes(confirmationStatus)
  const loginNotice = shouldToastConfirmation ? null : confirmationMessage
  const routeView =
    location.pathname === VERIFY_TWO_FACTOR_PATH
      ? 'two-factor'
      : location.pathname === SIGN_UP_PATH
        ? 'signup'
        : location.pathname === CONFIRMATION_SENT_PATH
          ? 'inbox'
          : 'login'
  const activeView = view === 'inbox' ? 'inbox' : routeView

  useEffect(() => {
    if (!shouldToastConfirmation || !confirmationMessage) return
    if (confirmationToastKeyRef.current === search) return

    confirmationToastKeyRef.current = search
    const toastId = window.setTimeout(() => {
      toast.success(confirmationMessage)
    }, 0)

    return () => window.clearTimeout(toastId)
  }, [confirmationMessage, search, shouldToastConfirmation])

  const handleLogin = async (values) => {
    const result = await signIn(values)
    if (result?.twoFactorRequired) {
      setView('two-factor')
      navigate(VERIFY_TWO_FACTOR_PATH)
    }
  }

  const handleSignup = async (values) => {
    const result = await signUp(values)
    const email = result?.email ?? values.email
    sessionStorage.setItem(CONFIRMATION_EMAIL_KEY, email)
    setPendingEmail(email)
    setView('inbox')
    navigate(CONFIRMATION_SENT_PATH, { state: { email }, replace: true })
  }

  if (activeView === 'inbox') {
    return (
      <div className="w-full max-w-md">
        <div key="inbox" className={TRANSITION}>
          <CheckInboxPanel
            email={pendingEmail}
            onBack={() => {
              setPendingEmail(null)
              sessionStorage.removeItem(CONFIRMATION_EMAIL_KEY)
              setView('login')
              navigate(SIGN_IN_PATH)
            }}
          />
        </div>
      </div>
    )
  }

  if (activeView === 'two-factor') {
    const handleVerify = async (values) => {
      await verifyTwoFactor(values)
      navigate('/', { replace: true })
    }

    const handleVerifyBackup = async (values) => {
      await verifyTwoFactorBackup(values)
      navigate('/', { replace: true })
    }

    return (
      <div className="w-full max-w-md">
        <div key="two-factor" className={TRANSITION}>
          <TwoFactorChallengeForm
            onVerify={handleVerify}
            onVerifyBackup={handleVerifyBackup}
            onBack={() => {
              setView('login')
              navigate(SIGN_IN_PATH, { replace: true })
            }}
          />
        </div>
      </div>
    )
  }

  const header = HEADERS[activeView]
  const toggleLabel =
    activeView === 'login'
      ? "Don't have access? Request an account."
      : 'Already initialised? Access system.'

  return (
    <div className="w-full max-w-md">
      <div key={activeView} className={TRANSITION}>
        <header className="mb-10 text-center lg:text-left">
          <h2 className="mb-2 text-3xl font-bold tracking-tight text-foreground">
            {header.title}
          </h2>
          <p className="text-muted-foreground">{header.subtitle}</p>
        </header>

        {activeView === 'login' ? (
          <LoginForm onSubmit={handleLogin} notice={loginNotice} />
        ) : (
          <SignupForm onSubmit={handleSignup} />
        )}
      </div>

      <p className="mt-8 text-center">
        <button
          type="button"
          onClick={() => {
            setView('login')
            navigate(activeView === 'login' ? SIGN_UP_PATH : SIGN_IN_PATH)
          }}
          className="text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          {toggleLabel}
        </button>
      </p>
    </div>
  )
}
